/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BallsGraph, TableAdvanced, Text } from "vr-react-components";
import { BackgroundPanel } from "vr-react-components";
import { Button } from "vr-react-components";
import { sendClaraIntent } from "../multiplayer/SocketConnection";
import { useState } from "react";
import { setClaraStatus } from "../store/slice/claraSlice";
import { getClaraIntent } from "../handlers/ClaraHandlers";

const CLARA_DISABLED = "The Clara host is not configured properly, check the configuration or try to restart the session.";
const UNSUPPORTED_ACTIONS = [
    "Monitoring with HERO",
    "Change the priority of a job",
    "Change the limit of a workstation"
]
const UNSUPPORTED_SUGGESTIONS = [
    "Search by name",
    "Search by folder"
]

const ClaraPanel = React.memo(({ height, width, onClose, ...otherAttributes }) => {
    const isClaraEnabled = useSelector((state) => state.clara.isClaraEnabled);
    const isClaraWaiting = useSelector((state) => state.clara.waiting);
    const claraResponse = useSelector((state) => state.clara.response);
    const claraStatus = useSelector((state) => state.clara.status);
    const claraEnv = useSelector((state) => state.clara.claraEnv);

    const inVRMode = useSelector((state) => state.private.device.inVRMode);
    const inCooperative = useSelector((state) => state.private.device.cooperative);
    const currentUserData = useSelector((state) => state.user.data);
    const room = useSelector((state) => state.room);

    const [intervalShowed, setIntervalShowed] = useState([0, 3])

    const dispatch = useDispatch()

    const iAmTheOwner = currentUserData.socketId === room.roomCreator?.socketId;
    const collidable = inCooperative ? (iAmTheOwner ? "collidable" : "") : "collidable"

    useEffect(() => {
        if (!claraResponse && ((inCooperative && iAmTheOwner) || !inCooperative)) sendClaraIntent(getClaraIntent());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getButtons = (parsedResponse) => {
        let buttons = []

        if (parsedResponse && Array.isArray(parsedResponse)) {
            parsedResponse.forEach((element) => {
                if (element.custom) {
                    element.custom.cards?.forEach((item) => {
                        if (!UNSUPPORTED_ACTIONS.includes(item.title))
                            buttons.push({
                                payload: item.payload,
                                text: item.title,
                                type: Button.constants.primary
                            });
                    });

                    element.custom.suggestions?.forEach((item) => {
                        if (!UNSUPPORTED_SUGGESTIONS.includes(item.title))
                            buttons.push({
                                payload: item.payload,
                                text: item.title,
                                type: Button.constants.dark
                            });
                    });

                    if (element.custom.hasOwnProperty("status"))
                        dispatch(setClaraStatus(element.custom.status))
                    else if (claraStatus !== "none") dispatch(setClaraStatus("none"))
                }

                element.buttons?.forEach((item) => {
                    if (!UNSUPPORTED_ACTIONS.includes(item.title))
                        buttons.push({
                            payload: item.payload,
                            text: item.title,
                            type: Button.constants.primary
                        });
                });
                if(claraResponse[0]?.text && JSON.stringify(claraResponse[0]?.text).includes("The version of this environment is")){
                    buttons.push({
                        payload: "/hi",
                        text: "Restart",
                        type: Button.constants.primary
                    });
                }
            });
        }

        const actions = buttons.filter(x => x.type === Button.constants.primary)
        const suggestions = buttons.filter((button) => button.type === Button.constants.dark)
        const buttonHeight =
            suggestions.length > 3 ? (height * 0.35) / buttons.length : height * 0.08;

        var table = {
            data: [
                ["Clara says:"],
                ...actions.map((x) => [
                    x.text
                ]),
            ],
            onRowClick: (i) => sendClaraIntent(buttons[i].payload)
        }

        return (
            <a-entity
                position={`0 1 0`}
            >
                {
                    actions.length &&
                    <TableAdvanced
                        width={!inVRMode ? 0.75 : 1.8}
                        height={!inVRMode ? 0.3 : 0.75}
                        textSizeDecimal={!inVRMode ? 50 : 25}
                        backgroundColor={"#0066B6"}
                        hoverRowColor={"#14A1DE"}
                        headerColor={"#121212"}
                        aframeClasses={collidable}
                        disabled={false}
                        position={!inVRMode ?
                            `0.005 ${(suggestions.length * buttonHeight) - 1.14} 0.001` :
                            `0 ${(suggestions.length * buttonHeight) - 1.36} 0.001`
                        }
                        {...table}
                    />
                }
                {
                    suggestions.map((button, i) => {
                        return (
                            <Button
                                key={i}
                                text={button.text}
                                type={button.type}
                                textAlign="left"
                                width={width * 0.9}
                                height={buttonHeight}
                                textSizeDecimal={50 / width}
                                aframeClasses={collidable}
                                onClickHandler={() => sendClaraIntent(button.payload)}
                                position={!inVRMode ?
                                    `0 ${(i * (buttonHeight + 0.005)) - 1.4} 0.01` :
                                    `0 ${(i * (buttonHeight + 0.005)) - 2} 0.01`
                                }
                            />
                        );
                    })
                }
            </a-entity>
        );
    };

    const getChatContent = () => {
        let isSwitchToWAEnabled = false;
        let parsedResponse = [];
        let summaryStatuses = [];
        if (claraResponse) {
            if (Array.isArray(claraResponse)) {
                claraResponse.forEach((element) => {
                    if (element.text)
                        parsedResponse += element.text + "\n";
                    else if (element.custom?.text && element.custom?.statuses_count) {
                        parsedResponse += element.custom.text.split('.')[0] + ".\n";
                        element.custom.statuses_count.forEach((stats) => {
                            summaryStatuses.push({
                                value: stats.count,
                                color: stats.color,
                                label: stats.name,
                            });
                        })
                    }
                });
            } else {
                parsedResponse = claraResponse;
            }
        } else {
            parsedResponse = CLARA_DISABLED;
        }

        let text = [parsedResponse];
        if (!isClaraEnabled) text = CLARA_DISABLED

        if (
            text.includes(
                "You want to receive general information about Workload Automation.",
            )
        ) {
            isSwitchToWAEnabled = true;
        }

        return (
            <>
                {
                    summaryStatuses.length &&
                    <BallsGraph width={width * 0.7} height={height * 0.6} data={summaryStatuses.slice(intervalShowed[0], intervalShowed[1])} />
                }
                {
                    summaryStatuses.length &&
                    <Button
                        text={">"}
                        width={width * 0.1}
                        height={height * 0.08}
                        type={Button.constants.primary}
                        disabled={intervalShowed[1] + 1 > 10}
                        textSizeDecimal={50}
                        position={inVRMode ? '0.5 0 0.001' : `0.3 0 0.001`}
                        aframeClasses={collidable}
                        onClickHandler={() => {
                            setIntervalShowed((old) => [old[0] + 1, old[1] + 1])
                        }}
                    />
                }
                {
                    summaryStatuses.length &&
                    <Button
                        text={"<"}
                        width={width * 0.1}
                        height={height * 0.08}
                        type={Button.constants.primary}
                        disabled={intervalShowed[0] - 1 < 0}
                        textSizeDecimal={50}
                        position={inVRMode ? '-0.5 0 0.001' : `-0.287 0 0.001`}
                        aframeClasses={collidable}
                        onClickHandler={() => setIntervalShowed((old) => [old[0] - 1, old[1] - 1])}
                    />
                }
                <Text
                    position={`${-width / 2 + width * 0.05} ${(height / 1.9) * 0.9} 0.001`}                    
                    baseline="top"
                    width={width * 0.9}
                    sizeDecimal={45 / width}
                    text={"Environment in use:"}
                />
                <Button
                    type={Button.constants.primary}
                    position={`${-width / 2 + width *0.575} ${(height / 1.9) * 0.89} 0.001`}
                    text={claraEnv && !(!claraResponse || claraResponse.length === 0) ? claraEnv : "Waiting..."}
                    width={width * 0.32}
                    height={height * 0.07}
                    aframeClasses={collidable}
                    textSizeDecimal={65}
                    onClickHandler={() => sendClaraIntent("/Change Environment")}
                />
                <Button
                    type={Button.constants.dark}
                    position={`${-width / 2 + width *0.87} ${(height / 1.9) * 0.89} 0.001`}
                    text={"Restart Conversation"}
                    width={width * 0.25}
                    height={height * 0.07}
                    aframeClasses={collidable}
                    textSizeDecimal={65}
                    onClickHandler={() => sendClaraIntent("/cancel")}
                />
                <Text
                    position={!inVRMode ? (`${-width / 2 + width * 0.05} ${(height / 2.2) * 0.9} 0.001`) : (`${-width / 2 + width * 0.05} ${(height / 2.2) * 0.8} 0.001`) }
                    baseline="top"
                    width={width * 0.9}
                    sizeDecimal={45 / width}
                    text={text}
                />
                {
                    <a-entity position={inVRMode ? `0 0.43 0` : `0 0 0`} width={0.4} height={0.5}>
                        {isClaraEnabled ? getButtons(claraResponse, isSwitchToWAEnabled) : null}
                    </a-entity>
                }
            </>
        );
    };

    return (
        <>
            <BackgroundPanel
                height={height}
                width={width}
                color="#121212"
                opacity={1}
                {...otherAttributes}
            >
                {
                    isClaraWaiting ?
                        <a-entity position={`0 0 0.001`} >
                            <a-circle
                                id="claraimg"
                                class={""}
                                radius={height * 0.1}
                                opacity={0.9}
                                animation__focusing="property: color; from: #515151; to: #B9B9B9; loop: true; dur: 1000; dir: alternate; easing: linear"
                            />
                            <a-image
                                height={height * 0.2}
                                width={height * 0.2}
                                position={inVRMode ? "0 0.025 0.001" : "0 0.01 0.001"}
                                src="#ClaraChat"
                            >
                            </a-image>
                        </a-entity>
                        : getChatContent()
                }
            </BackgroundPanel>
        </>
    );
});

export default ClaraPanel;
